import { forwardRef, useState, useEffect } from "react";
import { toast } from "sonner";
import { Phone, Mail, ArrowRight, MessageCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { WHATSAPP_URL } from "@/components/site/FloatingCall";

const productOptions = [
    "RCA / CASCO",
    "Asigurare Locuință",
    "Asigurare Călătorii",
    "Asigurare Sănătate / Viață",
    "Răspundere Civilă",
    "Credit Ipotecar",
    "Credit Imobiliar",
    "Credit Nevoi Personale",
    "Noua Casă",
    "Investiții",
    "Economisire",
    "Leasing",
    "Studii",
    "Pensii Private",
    "Altceva",
];

const ContactSection = forwardRef(function ContactSection({ presetProduct }, ref) {
    const [form, setForm] = useState({
        name: "", phone: "", email: "", product_type: presetProduct || "", message: "", contact_method: "Telefon",
    });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (presetProduct) {
            setForm((f) => ({ ...f, product_type: presetProduct }));
        }
    }, [presetProduct]);

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.phone || !form.email || !form.product_type) {
            toast.error("Te rog completează numele, telefon, email și tipul de produs.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/quote-requests", form);
            toast.success("Cererea ta a fost trimisă! Te contactăm în maxim 24h.");
            setForm({ name: "", phone: "", email: "", product_type: "", message: "", contact_method: "Telefon" });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section id="contact" ref={ref} data-testid="contact-section" className="bg-[#fcfbf8] py-24 lg:py-32">
            <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
                <div className="lg:col-span-5">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#0d3d2e]/60 mb-4">
                        05 — Contact
                    </div>
                    <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#0d3d2e] leading-[1.05] mb-8">
                        Cere o ofertă<br />
                        <em className="not-italic text-[#0d3d2e]/60">în 60 de secunde.</em>
                    </h2>
                    <p className="text-base lg:text-lg text-[#1a1a1a]/70 mb-10">
                        Lasă-ne datele tale și produsul de care ai nevoie. Te contactăm noi, gratuit,
                        cu oferte personalizate adaptate situației tale.
                    </p>

                    <div className="space-y-4">
                        <a
                            href="tel:0745643348"
                            data-testid="contact-phone-link"
                            className="flex items-center gap-4 group"
                        >
                            <div className="h-12 w-12 rounded-full bg-[#0d3d2e] text-[#fcfbf8] flex items-center justify-center group-hover:bg-[#0d3d2e]/90 transition-colors">
                                <Phone className="h-5 w-5" strokeWidth={1.5} />
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-[0.16em] text-[#0d3d2e]/60">Telefon</div>
                                <div className="font-display text-2xl text-[#0d3d2e]">0745 643 348</div>
                            </div>
                        </a>
                        <a
                            href="mailto:oferte@optimsafe.ro"
                            data-testid="contact-email-link"
                            className="flex items-center gap-4 group"
                        >
                            <div className="h-12 w-12 rounded-full bg-[#0d3d2e] text-[#fcfbf8] flex items-center justify-center group-hover:bg-[#0d3d2e]/90 transition-colors">
                                <Mail className="h-5 w-5" strokeWidth={1.5} />
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-[0.16em] text-[#0d3d2e]/60">Email</div>
                                <div className="font-display text-2xl text-[#0d3d2e]">oferte@optimsafe.ro</div>
                            </div>
                        </a>
                        <a
                            href={WHATSAPP_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="contact-whatsapp-link"
                            className="flex items-center gap-4 group"
                        >
                            <div className="h-12 w-12 rounded-full bg-[#25D366] text-white flex items-center justify-center group-hover:bg-[#1ebe57] transition-colors">
                                <MessageCircle className="h-5 w-5" strokeWidth={1.5} fill="white" />
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-[0.16em] text-[#0d3d2e]/60">WhatsApp · răspuns rapid</div>
                                <div className="font-display text-2xl text-[#0d3d2e]">Scrie-ne acum</div>
                            </div>
                        </a>
                    </div>
                </div>

                <div className="lg:col-span-7">
                    <form
                        onSubmit={submit}
                        data-testid="quote-form"
                        className="bg-white border border-[#0d3d2e]/15 rounded-2xl p-6 lg:p-10 space-y-5"
                    >
                        <div className="grid sm:grid-cols-2 gap-5">
                            <div>
                                <label className="block text-xs uppercase tracking-[0.16em] text-[#0d3d2e]/70 mb-2">
                                    Nume complet *
                                </label>
                                <input
                                    type="text"
                                    value={form.name}
                                    onChange={update("name")}
                                    required
                                    data-testid="form-name"
                                    className="w-full bg-[#fcfbf8] border border-[#0d3d2e]/20 rounded-md px-4 py-3 text-[#0d3d2e] focus:outline-none focus:border-[#0d3d2e] transition-colors"
                                    placeholder="Ion Popescu"
                                />
                            </div>
                            <div>
                                <label className="block text-xs uppercase tracking-[0.16em] text-[#0d3d2e]/70 mb-2">
                                    Telefon *
                                </label>
                                <input
                                    type="tel"
                                    value={form.phone}
                                    onChange={update("phone")}
                                    required
                                    data-testid="form-phone"
                                    className="w-full bg-[#fcfbf8] border border-[#0d3d2e]/20 rounded-md px-4 py-3 text-[#0d3d2e] focus:outline-none focus:border-[#0d3d2e] transition-colors"
                                    placeholder="07xx xxx xxx"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs uppercase tracking-[0.16em] text-[#0d3d2e]/70 mb-2">
                                Email *
                            </label>
                            <input
                                type="email"
                                value={form.email}
                                onChange={update("email")}
                                required
                                data-testid="form-email"
                                className="w-full bg-[#fcfbf8] border border-[#0d3d2e]/20 rounded-md px-4 py-3 text-[#0d3d2e] focus:outline-none focus:border-[#0d3d2e] transition-colors"
                                placeholder="email@exemplu.ro"
                            />
                        </div>

                        <div>
                            <label className="block text-xs uppercase tracking-[0.16em] text-[#0d3d2e]/70 mb-2">
                                Produs de interes *
                            </label>
                            <select
                                value={form.product_type}
                                onChange={update("product_type")}
                                required
                                data-testid="form-product"
                                className="w-full bg-[#fcfbf8] border border-[#0d3d2e]/20 rounded-md px-4 py-3 text-[#0d3d2e] focus:outline-none focus:border-[#0d3d2e] transition-colors"
                            >
                                <option value="">Alege produsul...</option>
                                {productOptions.map((p) => (
                                    <option key={p} value={p}>{p}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-xs uppercase tracking-[0.16em] text-[#0d3d2e]/70 mb-2">
                                Cum preferați să fiți contactat(ă)? *
                            </label>
                            <div className="flex flex-wrap gap-2" data-testid="form-contact-method">
                                {["Telefon", "WhatsApp", "E-mail"].map((m) => (
                                    <button
                                        key={m}
                                        type="button"
                                        onClick={() => setForm((f) => ({ ...f, contact_method: m }))}
                                        data-testid={`contact-method-${m.toLowerCase().replace(/[^a-z]/g, "")}`}
                                        className={`px-5 py-2.5 rounded-full border text-sm font-medium transition-all ${
                                            form.contact_method === m
                                                ? "bg-[#0d3d2e] text-[#fcfbf8] border-[#0d3d2e]"
                                                : "bg-[#fcfbf8] text-[#0d3d2e] border-[#0d3d2e]/25 hover:border-[#0d3d2e]/60"
                                        }`}
                                    >
                                        {m}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs uppercase tracking-[0.16em] text-[#0d3d2e]/70 mb-2">
                                Mesaj (opțional)
                            </label>
                            <textarea
                                value={form.message}
                                onChange={update("message")}
                                rows={4}
                                data-testid="form-message"
                                className="w-full bg-[#fcfbf8] border border-[#0d3d2e]/20 rounded-md px-4 py-3 text-[#0d3d2e] focus:outline-none focus:border-[#0d3d2e] transition-colors resize-none"
                                placeholder="Spune-mi mai multe despre nevoia ta..."
                            />
                        </div>

                        <p className="text-xs text-[#1a1a1a]/55">
                            Prin trimiterea formularului accepți să fii contactat de OPTIM SAFE
                            cu privire la cererea ta. Datele nu sunt partajate cu terți.
                        </p>

                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="form-submit"
                            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#0d3d2e] text-[#fcfbf8] px-8 py-3.5 text-base font-medium hover:bg-[#0d3d2e]/90 transition-all disabled:opacity-60"
                        >
                            {submitting ? "Se trimite..." : "Trimite cererea"}
                            <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
                        </button>
                    </form>
                </div>
            </div>
        </section>
    );
});

export default ContactSection;
